import type { User } from "./AdminUserAPI.types";
import type { OfferToApi } from "../components/offers/offerAdapter";
import type { PictureKeys } from "../constants/ratio";
import type {
  CreateOfferPlanToApi,
  OfferPlanToApi,
} from "../models/OfferPlan.types";
import type { AdvertisingEnum, Picture, TranslationsIndexes } from "../types";
import type { OfferPlanType } from "../types/offers";
import type { formToApiTranslations } from "../utils/translationAdapters/offer";

export interface CreateOfferPlanParams {
  projectSlug: string;
  plan: CreateOfferPlanToApi;
}

export interface UpdateOfferPlanParams {
  projectSlug: string;
  plan: OfferPlanToApi;
}

export interface UploadAdvertisingFileResponse {
  filename: string;
  video_url?: string;
}

export interface CreateAdvertisingResponse {
  uuid: string;
}

export interface GetOfferListResponse {
  data: OfferListItem[];
  indexes: Indexes;
  page: Page;
}

export interface GetOfferResponse {
  uuid: string;
  updated_at: string;
  translations_indexes: TranslationsIndexes;
  translations: Translation[];
  total_offers_count: number;
  temporal_restriction: boolean;
  storefront_project_uuid: string;
  rank: number;
  offer_plans: OfferPlanType[];
  medias: Media[];
  label: string;
  inserted_at: string;
  end_date: null;
  enabled: boolean;
  custom_params: null;
  begin_date: null;
  media_preview_uuid: string | null;
}

export interface OfferListItem {
  uuid: string;
  /** Type de l'offre (`svod`, `tvod_bundle`...), quand l'API le renvoie. */
  type?: string;
  updated_at: string;
  temporal_restriction: boolean;
  subscribers_count: number;
  storefront_project_uuid: string;
  rank: number;
  medias_count: number;
  label: string;
  inserted_at: string;
  end_date: null;
  enabled: boolean;
  begin_date: null;
  deletable: boolean;
}

export interface Indexes {
  uuid: Record<string, number[]>;
  storefront_project_uuid: Record<string, number[]>;
}

export interface Page {
  perPage: number;
  total: number;
  current: number;
}

export interface Media {
  uuid: string;
}

export interface Country {
  uuid: string;
  country?: string;
}

export interface CountryGroup {
  uuid: string;
  label?: string;
}

export type ProviderPlan = "stripe" | "paypal";

export interface Advertising {
  uuid: string;
  type: AdvertisingEnum;
  status: string;
  label: string;
}

export interface Translation {
  uuid: string;
  temp_pictures: null;
  pictures: Pictures;
  picture: Picture;
  name: string;
  language_uuid: string;
  language: string;
  description: string;
  default: boolean;
}

export interface CreateAdvertisingTranslationResponse {
  default: boolean;
  uuid: string;
  filename: string | null;
  duration: number | null;
  url: string;
  language_uuid: string;
  video_url: string | null;
}
export interface CreateOfferTranslationResponse {
  uuid: string;
  default: boolean;
  language_uuid: string;
  name: string | null;
  description: string | null;
  offer_uuid: string;
}

export interface OfferTranslationToCreate {
  uuid?: string;
  default: boolean;
  language: string;
  language_uuid: string;
  name: string;
  description: string;
  picture?: Picture;
}

export interface Pictures {
  cover_picture_3_4: CoverPicture;
  cover_picture_1_1: CoverPicture;
  cover_picture_16_9: CoverPicture;
  cover_picture_16_6: CoverPicture;
}

export interface CoverPicture {
  medium: string;
  low: string;
  high: string;
}

export interface GetMediaUrlParams {
  mediaUuid: string;
  language: string;
}

export type Chapters = {
  title: string;
  time: number;
}[];

export type CuepointList = number[];

export enum MediaType {
  VIDEO_HOSTED = "video_hosted",
  VIDEO_LIVE = "video_live",
  AUDIO_LIVE = "audio_live",
}

export interface Subtitle {
  fileName: string;
  label: string;
  language: string;
}

export interface GetMediaUrlResponse {
  ads: unknown[];
  chapters?: Chapters | null;
  cuepoints?: CuepointList;
  media_type: MediaType;
  media_uuid: string;
  offer_uuid: string | null;
  source: string;
  subtitles?: Subtitle[];
  thumbnails?: string;
  token: string;
  type: string;
  url: string;
  user: Pick<User, "uuid" | "email" | "first_name" | "last_name">;
}

interface OfferPlanFreecopy {
  label: string;
  enabled: boolean;
  offer_uuid: string;
  uuid: string;
}

interface Subscription {
  begin_date: string;
  end_date: string;
  offer_plan_uuid: string;
  status: string;
}

interface SubscriptionUser {
  begin_date: string;
}
export interface GetFreecopyResponse {
  activation_limit_date: string;
  code: string;
  days_duration: string | number;
  email: string;
  inserted_at: string;
  offer_plan: OfferPlanFreecopy;
  status: string;
  subscription: Subscription;
  subscription_user: SubscriptionUser;
  updated_at: string;
  uuid: string;
}

// A plan from the /admin/offer_plans listing endpoint (EDU/gift pickers) — always enabled okast plans.
export interface OfferPlanListItem {
  currency: string;
  enabled: boolean;
  frequency: number;
  platform: string;
  price: number;
  rental_duration: number;
  resolution: string;
  type: string;
  uuid: string;
  description: null;
  advertising_uuid: string | null;
  offer: OfferPlanFreecopy;
}

export interface GetOfferPlanListResponse {
  data: OfferPlanListItem[];
  indexes: Indexes;
  page: Page;
}

export interface UpdateOfferPlanResponse {
  uuid: string;
}

export interface PostFreecopyResponse {
  uuid: string;
  code: string;
}

export interface FreecopyCreate {
  offer_plan_uuid: string;
  activation_limit_date: string;
  days_duration: string | number;
  lang: string;
  email: string;
}

export interface FreecopyListItem {
  uuid: string;
  email: string;
  /** `activated`, `activable`, `ended` ou `expired`. */
  status: string;
  code: string;
  activation_limit_date: string;
  days_duration: string | number | null;
  inserted_at: string;
  updated_at: string;
  offer_plan: OfferPlanFreecopy;
  subscription: Pick<Subscription, "begin_date" | "end_date"> | null;
}

export interface GetFreecopyListResponse {
  data: FreecopyListItem[];
  indexes: Indexes;
  page: Page;
}

export interface FreecopyBatchItem {
  uuid: string;
  label: string;
  count: number;
  activated: number;
  inserted_at: string;
  updated_at: string;
}

export interface GetFreecopyBatchResponse {
  data: FreecopyBatchItem[];
}

export interface FreecopyUniqueCodeItem {
  uuid: string;
  label: string;
  code: string;
  /** Vrai : actif (`enabled`), faux : désactivé (`disabled`). */
  status: boolean;
  offer_plan_uuid: string;
  current_activations: number;
  max_activations: number;
  usage_limit_date: string | null;
  days_duration: string | number | null;
  activation_limit_date: string;
  inserted_at: string;
  updated_at: string;
}

export interface GetFreecopyUniqueCodeListResponse {
  data: FreecopyUniqueCodeItem[];
}

export interface FreecopyResendEmail {
  resend_email: true;
}

export interface FreecopyUpdate {
  offer_plan_uuid: string;
  subscription_begin_date?: string;
  subscription_end_date?: string;
  activation_limit_date?: string;
  days_duration?: string | number;
  email?: string;
}

export const OFFER_PLATFORM = {
  OKAST: "okast",
  APPLE: "apple",
  GOOGLE: "google",
  AMAZON: "amazon",
  ROKU: "roku",
  RETAILER: "retailer",
  FREE: "free",
} as const;

export type OfferPlatform =
  (typeof OFFER_PLATFORM)[keyof typeof OFFER_PLATFORM];

export interface UploadPictureParams {
  offerId: string;
  pictureFile: File;
  translationId: string;
  pictureFormat: PictureKeys;
  projectSlug: string;
}

export interface DeletePictureParams {
  offerId: string;
  translationId: string;
  projectSlug: string;
}

export interface UploadPictureResponse {
  picture: Picture;
  pictures: Picture;
  temp_pictures: Picture;
}

export type UpdatedOffer = Omit<
  GetOfferResponse,
  "translations" | "offer_plans"
> & {
  translations: ReturnType<typeof formToApiTranslations>["translations"];
  offer_plans: OfferToApi["offer_plans"];
};
export interface UpdateOfferParams {
  offer: UpdatedOffer;
  projectSlug: string;
}

export interface GetGiftListParams {
  projectSlug: string;
}

export interface GetGiftListResponse {
  data: Gift[];
  indexes: Indexes;
  page: Page;
  type: "gift";
}

export interface Gift {
  code: string;
  message: string;
  sender: Sender;
  email: string;
  uuid: string;
  inserted_at: string;
  updated_at: string;
  limit_date: string;
  to_name: string;
  from_name: string;
  subscription: SubscriptionGift | null;
  activated: boolean;
  offer_plan: OfferPlanGift;
  subscription_user: Sender | null;
  month_duration: number;
  start_date: string;
}

interface OfferPlanGift {
  enabled: boolean;
  uuid: string;
  offer_uuid: string;
}

interface Sender {
  email: string;
  uuid: string;
  last_name: null | string;
  first_name: null | string;
}

interface SubscriptionGift {
  uuid: string;
  end_date: string;
  begin_date: string;
}
